import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Alert, AlertTitle } from '@material-ui/lab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from 'utils/axiosInstance';
import browserHistory from 'utils/browserHistory';
import { InfoNoticeDTO } from 'utils/restApplicationClient';

const useStyles = makeStyles((theme) => ({
  alert: {
    backgroundColor: '#fffaf0',
    color: '#855d10',
    border: '1px solid #ffb74d',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    textAlign: 'left',
    alignItems: 'center',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '& .MuiAlert-icon': {
      alignItems: 'center',
      paddingTop: 0,
      paddingBottom: 0,
      marginRight: theme.spacing(1.5),
      color: '#ffa000',
    },
    '& .MuiAlert-message': {
      flex: 1,
      paddingTop: 0,
      paddingBottom: 0,
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
      paddingTop: 0,
      paddingLeft: theme.spacing(2),
      marginRight: 0,
    },
  },
  alertTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 15,
    lineHeight: 1.3,
    color: '#664d03',
  },
  description: {
    whiteSpace: 'pre-line',
    fontSize: 13,
    lineHeight: 1.4,
    marginTop: theme.spacing(0.25),
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  formControlLabel: {
    margin: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: 13,
      color: 'inherit',
    },
  },
  checkbox: {
    padding: theme.spacing(0.5),
    color: 'inherit',
    '&.Mui-checked': {
      color: 'inherit',
    },
  },
}));

const DISMISSED_IDS_KEY = '___SYSTEM_NOTICE_DISMISSED___';
const LAST_FETCH_AT_KEY = '___SYSTEM_NOTICE_LAST_FETCH___';
const CACHE_KEY = '___SYSTEM_NOTICE_CACHE___';
const TTL_MS = 60 * 60 * 1000;

const safeParse = <T,>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const getDismissedIds = (): number[] => {
  const parsed = safeParse<unknown[]>(localStorage.getItem(DISMISSED_IDS_KEY));
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((value): value is number => typeof value === 'number');
};

const getCachedNotice = (): InfoNoticeDTO | null =>
  safeParse<InfoNoticeDTO>(localStorage.getItem(CACHE_KEY));

const SystemNotice: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [notice, setNotice] = useState<InfoNoticeDTO | null>(() =>
    getCachedNotice(),
  );
  const [closedInSession, setClosedInSession] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const loadNotice = useCallback(async (): Promise<void> => {
    const lastFetchAt = Number(localStorage.getItem(LAST_FETCH_AT_KEY));
    if (
      Number.isFinite(lastFetchAt) &&
      lastFetchAt > 0 &&
      Date.now() - lastFetchAt < TTL_MS
    ) {
      return;
    }
    try {
      const { data } = await api.provideNotice();
      const fresh = data ?? null;
      localStorage.setItem(LAST_FETCH_AT_KEY, String(Date.now()));
      if (fresh) {
        localStorage.setItem(CACHE_KEY, JSON.stringify(fresh));
      } else {
        localStorage.removeItem(CACHE_KEY);
      }
      setNotice((previous) => {
        if (previous?.id !== fresh?.id) {
          setClosedInSession(false);
          setDoNotShowAgain(false);
        }
        return fresh;
      });
    } catch {
      // ignore - notice is optional
    }
  }, []);

  useEffect(() => {
    loadNotice();
    return browserHistory.listen(() => {
      loadNotice();
    });
  }, [loadNotice]);

  const isDismissed = useMemo(() => {
    if (notice?.id == null) return false;
    return getDismissedIds().includes(notice.id);
  }, [notice?.id]);

  if (!notice || closedInSession || isDismissed) {
    return null;
  }

  const handleClose = (): void => {
    if (doNotShowAgain) {
      localStorage.setItem(
        DISMISSED_IDS_KEY,
        JSON.stringify([...getDismissedIds(), notice.id]),
      );
    }
    setClosedInSession(true);
  };

  return (
    <Alert
      severity="info"
      variant="filled"
      className={classes.alert}
      action={
        <Box className={classes.actionBox}>
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox
                size="small"
                checked={doNotShowAgain}
                onChange={(event): void =>
                  setDoNotShowAgain(event.target.checked)
                }
                className={classes.checkbox}
              />
            }
            label={t('common.systemNotice.doNotShowAgain') as string}
          />
          <IconButton
            aria-label={t('common.systemNotice.close') as string}
            size="small"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <AlertTitle className={classes.alertTitle}>{notice.title}</AlertTitle>
      <Box className={classes.description}>{notice.description}</Box>
    </Alert>
  );
};

export default SystemNotice;
